import { Link } from "react-router-dom";
import { articles } from "../../../mocks/articles";

export default function EditorialShowcase() {
  const deeps = articles.filter((a) => a.category === "deep").slice(0, 3);
  const analyses = articles.filter((a) => a.category === "analysis");
  const pickOne = articles.filter((a) => a.category === "pick")[0];

  return (
    <section className="bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        {/* 头部：深度报道 + 封面 */}
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="section-kicker text-xs font-semibold text-secondary-700">专题策划 · 长篇系列</p>
            <h2 className="mt-2 font-serif text-2xl font-black text-foreground-950 md:text-3xl">深度报道 · 把瓜讲透</h2>
            <p className="mt-2 text-sm text-foreground-600">多信源求证、还原完整时间线，让每一次“吃瓜”都有分量。</p>
          </div>
          <div className="flex gap-2 text-xs">
            {["深度报道", "封面故事", "重点瓜分析"].map((t, i) => (
              <span key={t} className={`rounded-full px-3 py-1 whitespace-nowrap ${i === 1 ? "bg-accent-500 text-background-50" : "bg-background-100 text-foreground-600"}`}>
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* 深读主编大图 + 两列 */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* 主图大稿 */}
          <Link to={`/article/${deeps[0].slug}`} className="group relative overflow-hidden rounded-xl bg-foreground-950 lg:row-span-2 lg:col-span-1 lg:flex lg:flex-col">
            <div className="relative h-56 overflow-hidden lg:h-auto lg:flex-1 lg:min-h-0">
              <img
                src={deeps[0].image}
                alt={deeps[0].imageAlt}
                title={`${deeps[0].title} - 今日瓜报深度报道`}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/20 to-transparent" />
            </div>
            <div className="relative p-6">
              <span className="text-xs font-medium text-accent-300">深度报道 · {deeps[0].tag}</span>
              <h3 className="mt-2 font-serif text-xl font-black leading-snug text-background-50 md:text-2xl">
                {deeps[0].title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-background-100/80">{deeps[0].excerpt}</p>
              <div className="mt-4 flex items-center gap-3 text-xs text-background-100/70">
                <span>{deeps[0].author}</span>
                <span className="text-background-200/40">·</span>
                <span>{deeps[0].readMin} 分钟</span>
                <span className="ml-auto flex items-center gap-1 text-accent-300">
                  阅读全文 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </div>
            </div>
          </Link>

          {/* 右侧深度列表 */}
          <div className="space-y-4 lg:col-span-2">
            {deeps.slice(1).map((a) => (
              <Link
                key={a.slug}
                to={`/article/${a.slug}`}
                className="group flex flex-col gap-4 rounded-xl bg-background-100 p-4 transition-colors hover:bg-background-200/60 sm:flex-row"
              >
                <div className="w-full overflow-hidden rounded-lg sm:w-52 shrink-0">
                  <img src={a.image} alt={a.imageAlt} title={`${a.title} - 今日瓜报`} className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-xs text-secondary-700">深度报道</span>
                  <h3 className="mt-1.5 font-serif text-lg font-bold leading-snug text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {a.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{a.excerpt}</p>
                  <div className="mt-auto pt-3 flex items-center gap-3 text-xs text-foreground-500">
                    <span>{a.author}</span>
                    <span>·</span>
                    <span>{a.date}</span>
                    <span>·</span>
                    <span>{a.readMin}分钟</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* 下半部：重点瓜分析 双栏 */}
        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2">
          {/* 重点瓜分析列 */}
          <div>
            <div className="flex items-center justify-between border-b-2 border-primary-500 pb-3">
              <h3 className="flex items-center gap-2 font-serif text-lg font-bold text-foreground-950">
                <i className="ri-scales-3-line text-primary-600" aria-hidden="true"></i>
                重点瓜分析
              </h3>
              <button onClick={() => document.getElementById("feed")?.scrollIntoView({ behavior: "smooth" })} className="text-xs text-primary-700 hover:underline">
                更多 →
              </button>
            </div>
            <div className="mt-5 space-y-5">
              {analyses.map((a, idx) => (
                <Link key={a.slug} to={`/article/${a.slug}`} className="group flex gap-4">
                  <div className="w-16 shrink-0 pt-0.5">
                    <span className="font-serif text-4xl font-black text-secondary-200 transition-colors group-hover:text-primary-300">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="flex-1 border-b border-background-200 pb-5 group-last:border-0">
                    <h4 className="font-serif text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700 transition-colors md:text-lg">
                      {a.title}
                    </h4>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{a.excerpt}</p>
                    <span className="mt-2 inline-block rounded-full bg-accent-100 px-2.5 py-0.5 text-xs text-accent-800">#{a.tag}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* 编辑长评卡 */}
          <div>
            <div className="overflow-hidden rounded-2xl bg-accent-50 p-7 md:p-9">
              <div className="flex items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-quill-pen-fill" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="font-serif text-base font-bold text-accent-900">编辑部之声</p>
                  <p className="text-xs text-accent-700">EDITOR'S NOTE</p>
                </div>
              </div>
              <p className="mt-5 font-serif text-xl font-black leading-relaxed text-foreground-950 md:text-2xl">
                “看热闹的吃瓜，看门道的复盘。每一个被放大成热搜的瞬间，背后都有一条值得被讲清的逻辑线。”
              </p>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                我们坚持不为流量加工情绪，只为事实寻找依据。头条解读帮你抓住重点，重点瓜分析帮你厘清脉络，深度报道与封面故事则让时间说话——这是今日瓜报不变的选题逻辑。
              </p>
              {pickOne && (
                <Link to={`/article/${pickOne.slug}`} className="group mt-6 flex items-center justify-between rounded-xl bg-background-50 p-4 transition-colors hover:bg-background-100">
                  <div className="pr-3">
                    <span className="flex items-center gap-1 text-xs text-secondary-700"><i className="ri-star-fill text-primary-500" aria-hidden="true"></i>本周编辑推荐</span>
                    <p className="mt-1 font-serif text-sm font-bold text-foreground-900 group-hover:text-primary-700 transition-colors">{pickOne.title}</p>
                  </div>
                  <i className="ri-arrow-right-line shrink-0 text-primary-600" aria-hidden="true"></i>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}